// Canned responses (templates) management — list, create, edit, delete.
// Insert them from any composer via the template picker (file icon).

import { useRef, useState } from 'react';
import { FileText, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { RichTextEditor, type RichTextEditorHandle } from '@/components/RichTextEditor';
import {
  useTemplates,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
  type CannedTemplate,
} from '@/lib/templates';

export function TemplatesSettings() {
  const q = useTemplates();
  const del = useDeleteTemplate();
  const [editing, setEditing] = useState<CannedTemplate | 'new' | null>(null);
  const templates = q.data?.templates ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <FileText className="size-3.5" strokeWidth={2} />
          Templates
        </CardTitle>
        <Button size="sm" onClick={() => setEditing('new')} disabled={editing !== null}>
          <Plus className="size-3.5" strokeWidth={2} /> New template
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Canned responses the whole team can insert from any composer (the{' '}
          <FileText className="inline size-3" strokeWidth={2} /> button next to the paperclip).
        </p>

        {editing !== null && (
          <>
            <TemplateEditor
              initial={editing === 'new' ? null : editing}
              onDone={() => setEditing(null)}
            />
            <Separator />
          </>
        )}

        {q.isLoading && (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        )}
        {!q.isLoading && templates.length === 0 && editing === null && (
          <p className="py-4 text-sm text-muted-foreground">
            No templates yet. Try one for the reply you type most — pricing, onboarding steps,
            "we're on it".
          </p>
        )}
        {templates.map((t) => (
          <div key={t.id} className="flex items-start gap-3 rounded-md border p-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{t.name}</div>
              <div
                className="mt-0.5 line-clamp-2 text-xs text-muted-foreground"
                // Team-authored template preview (same trust level as the composer).
                dangerouslySetInnerHTML={{ __html: t.bodyHtml }}
              />
            </div>
            <div className="flex shrink-0 gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => setEditing(t)}
                aria-label={`Edit ${t.name}`}
              >
                <Pencil className="size-3.5" strokeWidth={1.75} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-muted-foreground hover:text-destructive"
                onClick={() => {
                  if (confirm(`Delete template "${t.name}"?`)) {
                    del.mutate(t.id, { onSuccess: () => toast.success('Template deleted') });
                  }
                }}
                aria-label={`Delete ${t.name}`}
              >
                <Trash2 className="size-3.5" strokeWidth={1.75} />
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function TemplateEditor({
  initial,
  onDone,
}: {
  initial: CannedTemplate | null;
  onDone: () => void;
}) {
  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const [name, setName] = useState(initial?.name ?? '');
  const [bodyHtml, setBodyHtml] = useState(initial?.bodyHtml ?? '');
  const rteRef = useRef<RichTextEditorHandle | null>(null);
  const busy = create.isPending || update.isPending;

  function save() {
    if (!name.trim() || !bodyHtml.trim()) {
      toast.error('Template needs a name and a body');
      return;
    }
    const opts = {
      onSuccess: () => {
        toast.success(initial ? 'Template updated' : 'Template created');
        onDone();
      },
      onError: (e: unknown) =>
        toast.error(e instanceof Error && e.message === 'duplicate_name'
          ? 'A template with that name already exists'
          : 'Save failed'),
    };
    if (initial) update.mutate({ id: initial.id, name: name.trim(), bodyHtml }, opts);
    else create.mutate({ name: name.trim(), bodyHtml }, opts);
  }

  return (
    <div className="space-y-2 rounded-md border p-3">
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder='Template name — e.g. "Pricing overview"'
        maxLength={100}
        className="h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
      />
      <div className="rounded-md border">
        <RichTextEditor
          ref={rteRef}
          defaultHtml={initial?.bodyHtml ?? ''}
          onChange={(v) => setBodyHtml(v.html)}
          placeholder="The response text…"
          minHeightClass="min-h-[120px]"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDone} disabled={busy}>
          Cancel
        </Button>
        <Button size="sm" onClick={save} disabled={busy || !name.trim() || !bodyHtml.trim()}>
          {busy && <Loader2 className="size-3.5 animate-spin" />}
          {initial ? 'Save changes' : 'Create'}
        </Button>
      </div>
    </div>
  );
}
